import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, MessageSquare, Clock, ArrowLeft } from 'lucide-react';
import { getAppConversations } from '../services/appsApi';
import AIApps from './AIApps';

const AppsPage = ({ onBack }) => {
  const [appConversations, setAppConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAIApps, setShowAIApps] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadAppConversations();
  }, []);

  const loadAppConversations = async () => {
    try {
      const conversations = await getAppConversations();
      setAppConversations(conversations);
    } catch (error) {
      console.error('Failed to load app conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectApp = (app) => {
    // Navigate to app conversation
    navigate(`/apps/conversation/new`, { 
      state: { 
        appId: app.id,
        appName: app.name,
        systemPrompt: app.systemPrompt,
        metadata: {
          ...app,
          icon: undefined // Remove icon to avoid serialization error
        }
      }
    });
    setShowAIApps(false);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <h1 className="text-xl font-semibold text-gray-900">AI Apps</h1>
            </div>
          </div>
          <button
            onClick={() => setShowAIApps(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Browse Apps
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading app conversations...</div>
          </div>
        ) : appConversations.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center py-16">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <Sparkles className="w-16 h-16 text-purple-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                No AI App Conversations Yet
              </h2>
              <p className="text-gray-600 mb-6">
                Start using specialized AI assistants to help with your tasks
              </p>
              <button
                onClick={() => setShowAIApps(true)}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Explore AI Apps
              </button>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Your App Conversations
            </h2>
            <div className="space-y-3">
              {appConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  onClick={() => navigate(`/apps/conversation/${conversation.id}`)}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <MessageSquare className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 mb-1">
                          {conversation.title}
                        </h3>
                        {conversation.metadata?.appName && (
                          <p className="text-sm text-purple-600 mb-1">
                            {conversation.metadata.appName}
                          </p>
                        )}
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(conversation.updatedAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Apps Modal */}
      {showAIApps && (
        <AIApps
          onClose={() => setShowAIApps(false)}
          onSelectApp={handleSelectApp}
        />
      )}
    </div>
  );
};

export default AppsPage;